import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * Mount/unmount with an exit transition (PRD §3.9): the layer mounts closed,
 * flips to `open` on the next frames (so the enter transition runs), and on
 * close stays mounted in `closed` until the exit transition ends. Re-opening
 * mid-exit simply flips back: CSS transitions reverse from the current value.
 */
export function usePresence(open: boolean, exitMs: number) {
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<'open' | 'closed'>('closed');

  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setState('open'));
      });
      return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
    }
    setState('closed');
    const t = window.setTimeout(() => setMounted(false), exitMs);
    return () => window.clearTimeout(t);
  }, [open, exitMs]);

  return { mounted: mounted || open, state };
}

const mediaSubscribers = new Map<string, (cb: () => void) => () => void>();
function subscribeMedia(query: string) {
  let fn = mediaSubscribers.get(query);
  if (!fn) {
    fn = (cb: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', cb);
      return () => mql.removeEventListener('change', cb);
    };
    mediaSubscribers.set(query, fn);
  }
  return fn;
}

/** One `matchMedia` subscription per query, SSR-safe. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    subscribeMedia(query),
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** ≥ md (768 px): desktop layout (sidebar, top bar search, popovers instead of sheets). */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/** Keeps the latest value in a ref (for listeners registered once). */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/** Document direction, kept in step with i18n's `<html dir>`. */
function subscribeDir(cb: () => void) {
  const obs = new MutationObserver(cb);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
  return () => obs.disconnect();
}

export function useDocumentDir(): 'rtl' | 'ltr' {
  return useSyncExternalStore(
    subscribeDir,
    () => (document.documentElement.dir === 'ltr' ? 'ltr' : 'rtl'),
    () => 'rtl',
  );
}
